import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { Box, Typography, useTheme } from '@mui/material';
import { Fund } from '../../services/dashboardService';

interface FundAUMChartProps {
  funds: Fund[];
}

const FundAUMChart: React.FC<FundAUMChartProps> = ({ funds }) => {
  const theme = useTheme();

  // Prepare data for the chart
  const chartData = funds.slice(0, 8).map((fund) => ({
    name: fund.name.length > 20 ? `${fund.name.substring(0, 20)}...` : fund.name,
    fullName: fund.name,
    aum: fund.aum / 1000000, // Convert to millions
    status: fund.status,
    riskLevel: fund.risk_level,
  }));

  const getBarColor = (riskLevel: string) => {
    switch (riskLevel) {
      case 'high':
        return theme.palette.error.main;
      case 'medium':
        return theme.palette.warning.main;
      case 'low':
        return theme.palette.success.main;
      default:
        return theme.palette.primary.main;
    }
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Box
          sx={{
            bgcolor: 'background.paper',
            p: 2,
            borderRadius: 1,
            border: `1px solid #2a2a2a`,
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            {data.fullName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            AUM: €{data.aum.toFixed(1)}M
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Status: {data.status}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Risk: {data.riskLevel}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  if (chartData.length === 0) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        height={300}
        bgcolor="background.default"
        borderRadius={1}
        sx={{ border: '1px dashed', borderColor: 'divider' }}
      >
        <Typography variant="body1" color="text.secondary">
          No fund data available
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        Fund Assets Under Management
      </Typography>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart
          data={chartData}
          margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 60,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
          <XAxis
            dataKey="name"
            angle={-45}
            textAnchor="end"
            height={80}
            fontSize={12}
            stroke={theme.palette.text.secondary}
          />
          <YAxis
            label={{ value: 'AUM (€M)', angle: -90, position: 'insideLeft' }}
            fontSize={12}
            stroke={theme.palette.text.secondary}
          />
          <Tooltip content={<CustomTooltip />} />
          <Bar dataKey="aum" radius={[4, 4, 0, 0]}>
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={getBarColor(entry.riskLevel)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default FundAUMChart;
